import { useEffect, useRef, useState, type AnchorHTMLAttributes, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { CheckOutlined, CopyOutlined } from "@ant-design/icons";
import { useUiText } from "./uiText";
import { cx } from "./fieldContext";
const COPIED_RESET_MS = 1600;

export type TextType = "secondary" | "success" | "warning" | "danger";

type EllipsisConfig = boolean | { rows?: number; tooltip?: ReactNode; expandable?: boolean; symbol?: ReactNode; onExpand?: () => void };
type CopyableConfig = boolean | { text?: string | (() => string); onCopy?: () => void; icon?: ReactNode; tooltips?: ReactNode };

interface TypographyBaseProps {
  type?: TextType;
  strong?: boolean;
  italic?: boolean;
  underline?: boolean;
  delete?: boolean;
  code?: boolean;
  mark?: boolean;
  keyboard?: boolean;
  disabled?: boolean;
  ellipsis?: EllipsisConfig;
  copyable?: CopyableConfig;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

function decorate(children: ReactNode, { strong, italic, underline, delete: del, code, mark, keyboard }: TypographyBaseProps): ReactNode {
  let node = children;
  if (keyboard) node = <kbd>{node}</kbd>;
  if (code) node = <code>{node}</code>;
  if (mark) node = <mark>{node}</mark>;
  if (underline) node = <u>{node}</u>;
  if (del) node = <del>{node}</del>;
  if (italic) node = <i>{node}</i>;
  if (strong) node = <strong>{node}</strong>;
  return node;
}

function textOf(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  return "";
}

function CopyButton({ config, children }: { config: Exclude<CopyableConfig, false>; children: ReactNode }) {
  const { text } = useUiText();
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const options = typeof config === "object" ? config : {};
  return (
    <button
      type="button"
      className={cx("ck-text-copy", copied && "is-copied")}
      aria-label={text(copied ? "Copied" : "Copy")}
      title={text(copied ? "Copied" : "Copy")}
      onClick={(event) => {
        event.stopPropagation();
        const value = typeof options.text === "function" ? options.text() : options.text ?? textOf(children);
        return navigator.clipboard?.writeText(value).then(() => {
          options.onCopy?.();
          setCopied(true);
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
        });
      }}
    >
      {copied ? <CheckOutlined /> : options.icon ?? <CopyOutlined />}
    </button>
  );
}

function classesFor(base: string, props: TypographyBaseProps) {
  const ellipsis = props.ellipsis;
  const rows = typeof ellipsis === "object" ? ellipsis.rows : undefined;
  return {
    className: cx(
      "ck-typo",
      base,
      props.type && `ck-typo--${props.type}`,
      props.disabled && "is-disabled",
      ellipsis && (rows && rows > 1 ? "ck-typo--clamp" : "ck-typo--ellipsis"),
      props.className,
    ),
    style: rows && rows > 1 ? ({ WebkitLineClamp: rows, ...props.style } as CSSProperties) : props.style,
    title: ellipsis && typeof ellipsis === "object" && typeof ellipsis.tooltip === "string" ? ellipsis.tooltip : ellipsis ? textOf(props.children) || undefined : undefined,
  };
}

const OWN_KEYS = ["type", "strong", "italic", "underline", "delete", "code", "mark", "keyboard", "disabled", "ellipsis", "copyable", "className", "style", "children"] as const;
function htmlRest<T extends object>(props: T) {
  const rest = { ...props } as Record<string, unknown>;
  for (const key of OWN_KEYS) delete rest[key];
  return rest;
}

export interface TextProps extends TypographyBaseProps, Omit<HTMLAttributes<HTMLSpanElement>, "children" | "className" | "style"> {}

/** Fließtext-Abschnitt (Ersatz für Antd `Typography.Text`, gleiche Props). */
export function Text(props: TextProps) {
  const { className, style, title } = classesFor("ck-text", props);
  return (
    <span className={className} style={style} title={title} {...htmlRest(props)}>
      {decorate(props.children, props)}
      {props.copyable ? <CopyButton config={props.copyable}>{props.children}</CopyButton> : null}
    </span>
  );
}

export interface ParagraphProps extends TypographyBaseProps, Omit<HTMLAttributes<HTMLDivElement>, "children" | "className" | "style"> {}

/** Absatz (Ersatz für Antd `Typography.Paragraph`). `ellipsis.expandable` zeigt einen Knopf zum Aufklappen (`symbol` als Beschriftung). */
export function Paragraph(props: ParagraphProps) {
  const { text } = useUiText();
  const [expanded, setExpanded] = useState(false);
  const ellipsis = props.ellipsis;
  const expandable = typeof ellipsis === "object" && ellipsis.expandable;
  const effective = expanded ? { ...props, ellipsis: false } : props;
  const { className, style, title } = classesFor("ck-paragraph", effective);
  const paragraph = (
    <div className={className} style={style} title={expanded ? undefined : title} {...htmlRest(props)}>
      {decorate(props.children, props)}
      {props.copyable ? <CopyButton config={props.copyable}>{props.children}</CopyButton> : null}
    </div>
  );
  if (!expandable || expanded) return paragraph;
  // Knopf außerhalb des geklemmten Absatzes, sonst schnitte die Zeilenbegrenzung ihn ab.
  return (
    <>
      {paragraph}
      {(
        <button
          type="button"
          className="ck-typo-expand"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded(true);
            if (typeof ellipsis === "object") return ellipsis.onExpand?.();
          }}
        >
          {typeof ellipsis === "object" && ellipsis.symbol != null ? ellipsis.symbol : text("more")}
        </button>
      )}
    </>
  );
}

export interface TitleProps extends TypographyBaseProps, Omit<HTMLAttributes<HTMLHeadingElement>, "children" | "className" | "style"> {
  level?: 1 | 2 | 3 | 4 | 5;
}

/** Überschrift (Ersatz für Antd `Typography.Title`). Schrift und Ornament folgen dem Oberflächenstil. */
export function Title({ level = 1, ...props }: TitleProps) {
  const Tag = `h${level}` as const;
  const { className, style, title } = classesFor(`ck-title ck-title--${level}`, props);
  return (
    <Tag className={className} style={style} title={title} {...htmlRest(props)}>
      {decorate(props.children, props)}
      {props.copyable ? <CopyButton config={props.copyable}>{props.children}</CopyButton> : null}
    </Tag>
  );
}

export interface TypographyRootProps extends HTMLAttributes<HTMLElement> {
  children?: ReactNode;
}

/** Lesesatz-Container (Ersatz für `<Typography>` als Hülle um Überschriften und Absätze). */
export function TypographyRoot({ className, ...rest }: TypographyRootProps) {
  return <article className={cx("ck-typography", className)} {...rest} />;
}

export interface LinkProps extends TypographyBaseProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children" | "className" | "style" | "type"> {}

/** Textlink (Ersatz für Antd `Typography.Link`). */
export function Link(props: LinkProps) {
  const { className, style, title } = classesFor("ck-link", props);
  const rest = htmlRest(props) as AnchorHTMLAttributes<HTMLAnchorElement>;
  return (
    <a
      className={className}
      style={style}
      title={title}
      {...rest}
      href={props.disabled ? undefined : rest.href}
      aria-disabled={props.disabled || undefined}
      onClick={props.disabled ? (event) => event.preventDefault() : rest.onClick}
    >
      {decorate(props.children, props)}
    </a>
  );
}
